import LinearProgress from '@mui/joy/LinearProgress';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDataGridState } from '@/hooks/useDataGridState';
import { useExamples } from '@/hooks/useSwr';
import {
  appExamplePath,
  appPublicDatasetsPath,
  appPublicPath,
} from '@/utils/constants';

import { DataGrid, DataGridPagination } from '../DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
} from '../DataGrid.utils';
import { COLUMNS_DEF } from '../ExamplesTable/components/Columns';
import { useExamplesTable } from '../ExamplesTable/hooks/useExamplesTable';

interface TableProps {
  datasetShareToken?: string;
  emptyState?: React.ReactNode;
  exampleCount?: number;
}

const pluralRules = new Intl.PluralRules('en');

export const PublicExamplesTable = ({
  datasetShareToken,
  emptyState,
  exampleCount,
}: TableProps) => {
  'use no memo';
  const { paginationModel, setPaginationModel } = useDataGridState({});
  const navigate = useNavigate();

  const {
    data: rows,
    isLoading,
    isValidating,
  } = useExamples(
    datasetShareToken
      ? {
          offset: paginationModel.pageIndex * paginationModel.pageSize,
          limit: paginationModel.pageSize,
        }
      : null,
    { datasetShareToken }
  );
  const [rowSelection] = useState({});
  const table = useExamplesTable({
    rows,
    paginationModel,
    onPaginationChange: setPaginationModel,
    rowSelection: rowSelection,
    columns: COLUMNS_DEF,
  });

  if (!rows) {
    return <LinearProgress />;
  }

  return rows.length ? (
    <>
      <DataGrid
        table={table}
        isLoading={isLoading || isValidating}
        emptyState={emptyState}
        onClick={(row, event) => {
          if (
            !emulateNativeClick(
              `/${appPublicPath}/${datasetShareToken}/${appPublicDatasetsPath}/${row.id}/${appExamplePath}`,
              event.nativeEvent
            )
          ) {
            navigate(
              `/${appPublicPath}/${datasetShareToken}/${appPublicDatasetsPath}/${row.id}/${appExamplePath}`
            );
          }
        }}
        onMiddleClick={(row) => {
          if (
            !emulateNativeMiddleClick(
              `/${appPublicPath}/${datasetShareToken}/${appPublicDatasetsPath}/${row.id}/${appExamplePath}`
            )
          ) {
            navigate(
              `/${appPublicPath}/${datasetShareToken}/${appPublicDatasetsPath}/${row.id}/${appExamplePath}`
            );
          }
        }}
      />

      <div className="flex items-center justify-between">
        <div className="text-sm text-tertiary">
          {exampleCount != null &&
            {
              one: `1 example in total`,
              two: `${exampleCount} examples in total`,
              few: `${exampleCount} examples in total`,
              other: `${exampleCount} examples in total`,
            }[pluralRules.select(exampleCount)]}
        </div>

        <DataGridPagination table={table} />
      </div>
    </>
  ) : (
    <>{emptyState}</>
  );
};
